import { css } from "@emotion/react";
import { theme } from "@product/scmp-sdk";
import mapValues from "lodash/mapValues";
import merge from "lodash/merge";

import type { ResponsiveVariants } from "./responsive";
import { setResponsiveVariantStyles } from "./responsive";

const cssVariableKeys = {
  contentAlignmentPaddingInline: "--content-alignment-padding-inline",
  headerAlignmentPaddingInline: "--header-alignment-padding-inline",
  pageAlignmentPaddingInline: "--page-alignment-padding-inline",
  pageMaxWidth: "--page-max-width",
} as const;

export const cssVariableValues = mapValues(cssVariableKeys, mapCssVariableKeyToValue);

type CSSVariableValue<CSSVariableKey extends string> = `var(${CSSVariableKey})`;
function mapCssVariableKeyToValue<T extends string>(key: T): CSSVariableValue<T> {
  return `var(${key})`;
}

export const setAlignmentVariables = (
  responsivePadding?: Partial<ResponsiveVariants<number>>,
) => css`
  /* stylelint-disable csstree/validator */
  ${cssVariableKeys.pageMaxWidth}: 1560px;
  ${cssVariableKeys.pageAlignmentPaddingInline}: 16px;
  ${cssVariableKeys.headerAlignmentPaddingInline}: 0;

  ${theme.breakpoints.up("tablet")} {
    ${cssVariableKeys.pageAlignmentPaddingInline}: 28px;
    ${cssVariableKeys.headerAlignmentPaddingInline}: 24px;
  }

  ${theme.breakpoints.up("desktop")} {
    ${cssVariableKeys.pageAlignmentPaddingInline}: 28px;
    ${cssVariableKeys.headerAlignmentPaddingInline}: 12px;
  }

  ${setResponsiveVariantStyles(
    merge({ desktopUp: 8, mobileUp: 4, tabletUp: 8 }, responsivePadding),
    variant => css`
      ${cssVariableKeys.contentAlignmentPaddingInline}: ${`${variant}px`};
    `,
  )}/* stylelint-enable csstree/validator */
`;

// This expands the elements to 100vw
export const setFullViewportWidthStyles = () => css`
  position: relative;
  inset-inline-start: 50%;
  inset-inline-end: 50%;

  inline-size: 100vw;
  margin-inline: -50vw;
`;

export const setFullViewportWidthBackgroundStyles = (backgroundColor: string) => css`
  ${setFullViewportWidthStyles()}
  position: absolute;
  inset-block-start: 0;
  z-index: -1;

  block-size: 100%;

  background-color: ${backgroundColor};
`;

// This value is for elements inside full viewport width container to match content alignment
export const fullViewportWidthWithMaxPageWidthPaddingInline = `calc(${cssVariableValues.pageAlignmentPaddingInline} +
          ${cssVariableValues.contentAlignmentPaddingInline})`;

// This expands the elements under App.PageContainer to match the full viewport width with max at page width
export const setFullViewportWidthWithMaxPageWidthStyles = () => css`
  inline-size: 100vw;
  max-inline-size: ${cssVariableValues.pageMaxWidth};
  margin-inline: calc(0px - ${fullViewportWidthWithMaxPageWidthPaddingInline});
`;

// This expands the elements under App.PageContainer to match the content width
export const setFullContentWidthStyles = () => css`
  margin-inline: calc(0px - ${cssVariableValues.contentAlignmentPaddingInline});
`;

export const alignmentCssVariableValues = {
  ...cssVariableValues,
  fullViewportWidthWithMaxPageWidthPaddingInline,
};
